/**
 * 弱网提示处理
 * @author haoru
 */

interface IOptions {
  timeout?: number;
}

// 弱网超时设置
const DEFAULT_TIMEOUT = 2 * 1000;
// const DEBUG_LOG = false && process.env.NX_APP_ENV !== 'prod';
const DEBUG_LOG = false;

/**
 * 事件监听兼容处理
//  * @param {*} evtTarget  事件监听目标
 * @param {*} evtName   事件名
 * @param {*} callback  回调函数
 * @param {*} options  相关参数设置
 */
function addMonitor(
  evtTarget: Element | Window | Document,
  evtName: string,
  callback: EventListenerOrEventListenerObject,
  options?: boolean | AddEventListenerOptions
) {
  if ('attachEvent' in evtTarget) {
    return evtTarget.attachEvent(`on${evtName}`, callback);
  }
  return evtTarget.addEventListener(evtName, callback, options);
}

/**
 * 事件监听解绑兼容处理
 * @param {*} evtTarget  事件监听目标
 * @param {*} evtName   事件名
 * @param {*} callback  事件回调
 */
function removeMonitor(
  evtTarget: Element | Window | Document,
  evtName: string,
  callback: EventListenerOrEventListenerObject,
  options?: boolean | AddEventListenerOptions
) {
  if ('attachEvent' in evtTarget) {
    return evtTarget.detachEvent(`on${evtName}`, callback);
  }
  return evtTarget.removeEventListener(evtName, callback, options);
}

/**
 * 判断是否支持pushState
 */
function supportsPushState() {
  const ua = window.navigator.userAgent;
  if (
    (ua.indexOf('Android 2.') !== -1 || ua.indexOf('Android 4.0') !== -1) &&
    ua.indexOf('Mobile Safari') !== -1 &&
    ua.indexOf('Chrome') === -1 &&
    ua.indexOf('Windows Phone') === -1
  ) {
    return false;
  }
  return window.history && 'pushState' in window.history;
}

class WeakNetwork {
  // 弱网回调超时执行器
  private timmer: null | NodeJS.Timer;

  // 弱网回调超时时间
  private timeout: number;

  // 每个页面只执行一次弱网回调
  private done = false;

  // 执行中的请求数
  private requestCount = 0;

  private static instance: null | WeakNetwork;

  private urlChangeBindThis: () => void;

  /** 暂存原始的pushState */
  private oldPushState: History['pushState'];

  private proxyPushState: History['pushState'] = (data, title, url) => {
    this.urlChange();
    return this.oldPushState.call(window.history, data, title, url);
  };

  private log(...args) {
    DEBUG_LOG &&
      // eslint-disable-next-line no-console
      console.log(
        performance.now(),
        ...args,
        `requestCount=${this.requestCount}`
      );
  }

  private urlChange() {
    this.timmer && clearTimeout(this.timmer);
    this.done = false;
  }

  private destroy() {
    // 解绑事件
    removeMonitor(
      window,
      supportsPushState() ? 'popstate' : 'hashchange',
      this.urlChangeBindThis
    );
    // 还原方法
    window.history.pushState = this.oldPushState;
  }

  public static getInstance(options?: IOptions) {
    if (!WeakNetwork.instance) {
      WeakNetwork.instance = new WeakNetwork();
    }

    return WeakNetwork.instance;
  }

  constructor() {
    this.timeout = DEFAULT_TIMEOUT;

    this.urlChangeBindThis = this.urlChange.bind(this);

    // 记录原始方法
    this.oldPushState = window.history.pushState;

    // 代理原始方法
    window.history.pushState = this.proxyPushState.bind(this);

    addMonitor(
      window,
      supportsPushState() ? 'popstate' : 'hashchange',
      this.urlChangeBindThis
    );
  }

  public removeRequestCount(url?: string) {
    this.requestCount -= 1;
    this.log('removeRequest', url);
  }

  public addRequestCount(url?: string) {
    this.requestCount += 1;
    this.log('addRequest', url);
  }

  public mayDoTimeout(fn: () => void, url?: string) {
    this.timmer && clearTimeout(this.timmer);
    this.timmer = setTimeout(() => {
      if (this.requestCount > 0 && !this.done && typeof fn === 'function') {
        this.done = true;
        fn();
        this.log('doTimeout', url);
      }
    }, this.timeout);
  }
}

export default WeakNetwork.getInstance();
